import {
  ButtonLink,
  cn,
  loginPath,
  useDocumentTitle,
  useSession,
} from "@bodemleven/course-kit/react";
import {
  ArrowRight,
  LandPlot,
  Lightbulb,
  Microscope,
  ShieldCheck,
  Sprout,
  TestTube,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useShellCopy } from "../i18n/useShellCopy.ts";
import { paths } from "../paths.ts";

const STEP_ICONS = {
  land: LandPlot,
  sample: TestTube,
  research: Microscope,
  advice: Sprout,
} as const satisfies Record<string, LucideIcon>;

const STEP_ORDER = ["land", "sample", "research", "advice"] as const;

/** `/`: public introduction to the lab and the way into the workspace. */
export const HomePage = (): ReactNode => {
  const { home } = useShellCopy();
  const { user, loading } = useSession();
  useDocumentTitle(home.title);

  const cta = user ? (
    <ButtonLink to={paths.workspace()} size="lg">
      {home.workspaceCta}
      <ArrowRight aria-hidden="true" className="size-5" />
    </ButtonLink>
  ) : (
    <ButtonLink to={loginPath(paths.workspace())} size="lg">
      {home.signInCta}
      <ArrowRight aria-hidden="true" className="size-5" />
    </ButtonLink>
  );

  return (
    <div className="pb-16">
      <section className="border-b border-border bg-card">
        <div
          className={cn(
            "mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 sm:py-16",
            "lg:grid-cols-[3fr_2fr] lg:items-center",
          )}>
          <div>
            <p className="text-base font-semibold tracking-wide text-primary uppercase">
              {home.eyebrow}
            </p>
            <h1 className="mt-3 text-4xl font-semibold sm:text-5xl lg:text-6xl">{home.heading}</h1>
            <p className="mt-5 max-w-[60ch] text-xl text-muted-foreground">{home.intro}</p>
            <div className="mt-8 flex min-h-13 flex-col items-start gap-3">
              {!loading && cta}
              {user && (
                <p className="text-base text-muted-foreground">{home.signedInAs(user.name)}</p>
              )}
            </div>
          </div>
          <div
            aria-hidden="true"
            className="relative mx-auto hidden aspect-square w-full max-w-sm lg:block">
            <div className="absolute inset-0 rounded-full bg-primary/10" />
            <div
              className={cn(
                "absolute inset-[12%] rounded-full border-2 border-dashed",
                "border-primary/30",
              )}
            />
            <div
              className={cn(
                "absolute inset-[28%] flex items-center justify-center rounded-full",
                "bg-primary text-primary-foreground shadow-lg",
              )}>
              <Microscope className="size-20" />
            </div>
            <span className="absolute top-[8%] left-[18%] size-6 rounded-full bg-accent/70" />
            <span className="absolute right-[10%] bottom-[22%] size-9 rounded-full bg-highlight/80" />
            <span className="absolute bottom-[8%] left-[30%] size-4 rounded-full bg-warning/50" />
          </div>
        </div>
      </section>

      <section aria-labelledby="home-steps" className="mx-auto max-w-6xl px-4 pt-14 sm:px-6">
        <h2 id="home-steps" className="text-3xl font-semibold">
          {home.stepsHeading}
        </h2>
        <p className="mt-2 text-lg text-muted-foreground">{home.stepsIntro}</p>
        <ol className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STEP_ORDER.map((key, index) => {
            const Icon = STEP_ICONS[key];
            const step = home.steps[key];
            return (
              <li
                key={key}
                className={cn(
                  "relative flex flex-col gap-3 rounded-2xl border border-border bg-card p-5",
                  "shadow-sm",
                )}>
                <span className="flex items-center justify-between gap-3">
                  <span
                    className={cn(
                      "flex size-12 items-center justify-center rounded-xl bg-primary/10",
                      "text-primary",
                    )}>
                    <Icon aria-hidden="true" className="size-6" />
                  </span>
                  <span className="text-base font-semibold text-muted-foreground">
                    {home.stepLabel(index + 1)}
                  </span>
                </span>
                <h3 className="text-xl font-semibold">{step.title}</h3>
                <p className="text-muted-foreground">{step.body}</p>
              </li>
            );
          })}
        </ol>
      </section>

      <section aria-labelledby="home-look" className="mx-auto max-w-6xl px-4 pt-14 sm:px-6">
        <div className="grid gap-8 lg:grid-cols-[2fr_3fr]">
          <div>
            <h2 id="home-look" className="text-3xl font-semibold">
              {home.lookHeading}
            </h2>
            <p className="mt-2 text-lg text-muted-foreground">{home.lookIntro}</p>
            <div className="mt-6 flex gap-3 rounded-2xl bg-header p-5 text-header-foreground">
              <ShieldCheck aria-hidden="true" className="mt-1 size-6 shrink-0 text-highlight" />
              <div>
                <h3 className="text-lg font-semibold">{home.labHeading}</h3>
                <p className="mt-1 text-header-foreground/85">{home.labBody}</p>
              </div>
            </div>
          </div>
          <ul className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
            {home.look.map((item) => (
              <li
                key={item.title}
                className={cn(
                  "flex gap-4 rounded-2xl border border-border bg-card p-5",
                  "sm:flex-col lg:flex-row",
                )}>
                <Lightbulb aria-hidden="true" className="mt-1 size-6 shrink-0 text-primary" />
                <div>
                  <h3 className="text-lg font-semibold">{item.title}</h3>
                  <p className="mt-1 text-muted-foreground">{item.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-12 flex justify-center">{!loading && cta}</div>
      </section>
    </div>
  );
};
